import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, FileText, Scale } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const articles = [
  {
    number: '01',
    title: 'Objet et champ d\'application',
    content: `Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme CyberGuard, outil d'audit de sécurité informatique automatisé. En accédant à la plateforme, l'utilisateur accepte sans réserve les présentes conditions.`,
  },
  {
    number: '02',
    title: 'Utilisation autorisée',
    content: `CyberGuard est exclusivement destiné à l'audit de sécurité de systèmes informatiques dont l'utilisateur est le propriétaire légitime ou pour lesquels il dispose d'une autorisation écrite, explicite et préalable de la personne ou de l'entité responsable dudit système. Toute utilisation dans un cadre professionnel doit être conforme aux politiques internes de sécurité de l'organisation concernée.`,
  },
  {
    number: '03',
    title: 'Utilisations interdites et sanctions pénales',
    content: `Il est formellement et strictement interdit d'utiliser CyberGuard pour :
    
• Scanner, analyser, sonder ou tester des systèmes informatiques, réseaux ou infrastructures tiers sans autorisation explicite et écrite de leur propriétaire.
• Collecter des données personnelles ou confidentielles de manière illicite.
• Conduire des attaques informatiques, même simulées, sur des systèmes non autorisés.
• Toute activité contraire aux lois et réglementations en vigueur.

Conformément aux articles 323-1 à 323-7 du Code pénal français :
— L'accès frauduleux à un système informatique est passible de 2 ans d'emprisonnement et 60 000€ d'amende.
— En cas d'altération ou de suppression de données, la peine peut atteindre 3 ans d'emprisonnement et 100 000€ d'amende.
— En cas d'entrave au fonctionnement d'un système, la peine peut atteindre 5 ans d'emprisonnement et 150 000€ d'amende.`,
  },
  {
    number: '04',
    title: 'Responsabilité de l\'utilisateur',
    content: `L'utilisateur est seul et entièrement responsable de l'usage qu'il fait de la plateforme CyberGuard. En lançant un audit, l'utilisateur certifie :

• Être le propriétaire du système cible ou disposer d'une autorisation écrite explicite.
• Utiliser CyberGuard dans un cadre légal et éthique.
• Ne pas utiliser les résultats des audits à des fins malveillantes ou illicites.

CyberGuard décline toute responsabilité en cas d'utilisation illicite, abusive ou non conforme aux présentes conditions.`,
  },
  {
    number: '05',
    title: 'Compte et sécurité',
    content: `L'utilisateur crée un compte avec une adresse email valide qu'il doit confirmer grâce au lien reçu à l'inscription. L'audit offert n'est disponible qu'une fois l'adresse confirmée, et une seule fois par compte.

L'utilisateur garde ses identifiants confidentiels. Il peut activer la double authentification dans ses paramètres ; elle est obligatoire pour les comptes administrateurs. Il lui appartient de conserver ses codes de secours en lieu sûr : sans son téléphone ni ses codes, l'accès au compte ne peut être rétabli qu'après vérification de son identité par email à contact@cyber-guard.fr.`,
  },
  {
    number: '06',
    title: 'Vérification des domaines et analyse approfondie',
    content: `Certaines fonctions ne sont disponibles que sur un domaine dont l'utilisateur a prouvé le contrôle en publiant un enregistrement DNS fourni par CyberGuard : l'analyse approfondie et la surveillance hebdomadaire (plans Pro et Enterprise). L'enregistrement est revérifié avant chaque scan programmé ; s'il est retiré, la surveillance est suspendue.

L'analyse approfondie reste non intrusive : elle vérifie la présence de quelques fichiers sensibles connus (sans en conserver le contenu), les versions de chiffrement acceptées, la version du CMS et la qualité de la politique de sécurité du contenu, et liste les sous-domaines publics. Elle ne tente aucune exploitation de faille. Les cibles qui pointent vers une adresse réseau interne sont refusées.`,
  },
  {
    number: '07',
    title: 'Offre agences (plan Enterprise)',
    content: `Le plan Enterprise permet à une agence ou un prestataire d'auditer les sites de ses clients, de créer des fiches clients, de produire des rapports PDF à sa propre marque et de partager des rapports par lien.

• L'agence garantit disposer, pour chaque site audité, de l'autorisation de son client conformément à l'article 02.
• Elle garantit détenir les droits sur le nom et le logo qu'elle utilise. CyberGuard ne revendique aucun droit sur ces éléments et ne les utilise que pour personnaliser les rapports de l'agence.
• Pour les données de ses clients qu'elle enregistre dans le service, l'agence est responsable du traitement et CyberGuard agit en tant que sous-traitant, dans les conditions décrites dans la politique de confidentialité.
• Un lien de partage donne accès au rapport à toute personne qui le possède, jusqu'à son expiration ou sa désactivation par l'agence. L'agence choisit à qui elle le transmet. Les liens cessent de fonctionner si l'abonnement Enterprise prend fin.`,
  },
  {
    number: '08',
    title: 'Abonnements et paiement',
    content: `Les plans payants sont des abonnements mensuels, aux prix affichés sur la page Tarifs au moment de la souscription. Le paiement est réalisé par carte bancaire via Stripe ; CyberGuard n'a jamais accès aux numéros de carte. L'abonnement se renouvelle automatiquement chaque mois.

L'utilisateur peut résilier à tout moment depuis la page Facturation. La résiliation prend effet à la fin de la période en cours, déjà payée ; l'accès au plan reste ouvert jusqu'à cette date. Les quotas d'audits sont mensuels et ne se reportent pas d'un mois sur l'autre ; les scans de surveillance programmés ne sont pas décomptés du quota.`,
  },
  {
    number: '09',
    title: 'Limitation de responsabilité',
    content: `CyberGuard est fourni "en l'état" et ne garantit pas l'exhaustivité des résultats d'audit. Les rapports générés sont à titre informatif et ne constituent pas une certification de sécurité. CyberGuard ne saurait être tenu responsable des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser la plateforme.`,
  },
  {
    number: '10',
    title: 'Protection des données personnelles',
    content: `CyberGuard collecte et traite les données personnelles conformément au Règlement Général sur la Protection des Données (RGPD — Règlement UE 2016/679), uniquement pour fournir le service. Le détail des données traitées, de leurs durées de conservation, des sous-traitants et des droits de l'utilisateur figure dans la politique de confidentialité (cyber-guard.fr/confidentialite). Pour toute demande : contact@cyber-guard.fr.`,
  },
  {
    number: '11',
    title: 'Propriété intellectuelle',
    content: `L'ensemble des éléments constituant la plateforme CyberGuard (interface, code, algorithmes, textes, marque) sont la propriété exclusive de CyberGuard et sont protégés par les lois relatives à la propriété intellectuelle. Toute reproduction, représentation ou exploitation non autorisée est interdite.

Les rapports d'audit produits pour l'utilisateur peuvent être librement utilisés, imprimés et transmis par celui-ci, notamment à ses clients ; en plan Enterprise, ils peuvent l'être sous la marque de l'agence.`,
  },
  {
    number: '12',
    title: 'Modification des conditions',
    content: `CyberGuard se réserve le droit de modifier les présentes conditions à tout moment. Les utilisateurs seront informés de toute modification substantielle par email ou via une notification sur la plateforme. La poursuite de l'utilisation du service après notification vaut acceptation des nouvelles conditions.`,
  },
  {
    number: '13',
    title: 'Droit applicable et juridiction',
    content: `Les présentes conditions sont soumises au droit français. En cas de litige, et à défaut de résolution amiable, les tribunaux compétents de Paris seront seuls compétents pour connaître du différend.`,
  },
  {
    number: '14',
    title: 'Contact',
    content: `Pour toute question relative aux présentes conditions ou à l'utilisation de la plateforme :
    
Email : contact@cyber-guard.fr
Site : https://cyber-guard.fr`,
  },
];

export default function CGV() {
  useSEO('/cgv');
  return (
    <div className="min-h-screen bg-[#09090b] text-white">

      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-500" />
            <span className="font-mono text-lg font-black text-white uppercase tracking-tighter">CyberGuard</span>
          </Link>
          <Link to="/" className="text-zinc-500 hover:text-white font-mono text-xs uppercase tracking-widest transition-colors">
            ← Retour
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-16 space-y-12">

        {/* Titre */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20">
            <Scale className="w-3 h-3 text-blue-400" />
            <span className="text-blue-400 text-[10px] font-mono uppercase tracking-widest">Conditions Générales d'Utilisation</span>
          </div>
          <h1 className="text-4xl font-black font-mono uppercase tracking-tighter text-white">
            CGU — CyberGuard
          </h1>
          <p className="text-zinc-500 font-mono text-xs">Dernière mise à jour : 15 octobre 2026</p>
        </div>

        {/* Avertissement principal */}
        <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-6 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-mono font-bold text-sm uppercase tracking-widest mb-2">
              Avertissement légal important
            </p>
            <p className="text-zinc-300 font-mono text-sm leading-relaxed">
              CyberGuard est un outil d'audit de sécurité réservé aux professionnels autorisés. Toute utilisation sur des systèmes dont vous n'êtes pas propriétaire ou pour lesquels vous ne disposez pas d'une autorisation écrite explicite est strictement interdite et constitue une infraction pénale au titre de l'article 323-1 du Code pénal français, passible de <span className="text-white font-bold">2 ans d'emprisonnement</span> et <span className="text-white font-bold">60 000€ d'amende</span>.
            </p>
          </div>
        </div>

        {/* Articles */}
        <div className="space-y-8">
          {articles.map((article) => (
            <div key={article.number} className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 flex items-center justify-center rounded-sm shrink-0">
                  <span className="text-blue-400 font-mono text-[10px] font-black">{article.number}</span>
                </div>
                <h2 className="text-white font-mono font-bold text-sm uppercase tracking-widest">
                  {article.title}
                </h2>
              </div>
              <div className="pl-11">
                <p className="text-zinc-400 font-mono text-xs leading-relaxed whitespace-pre-line">
                  {article.content}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer légal */}
        <div className="border-t border-zinc-800 pt-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-zinc-600" />
            <span className="text-zinc-600 font-mono text-[10px] uppercase">CyberGuard — CGU v2.0 — Octobre 2026</span>
          </div>
          <Link to="/" className="text-blue-500 hover:text-blue-400 font-mono text-xs uppercase transition-colors">
            Retour à l'accueil →
          </Link>
        </div>
      </main>
    </div>
  );
}
